import type { ChangeEvent, ComponentProps, HTMLProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ResponsiveValue } from 'styled-system';
import {
  backgroundPosition,
  backgroundSize,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  color,
  fontSize,
  height,
  lineHeight,
  maxWidth,
  space,
  themeGet,
  width,
} from 'styled-system';

import { breakpoint } from '../theme/helper';
import { TYPOGRAPHY_BR1_FONT_SIZE, TYPOGRAPHY_BR1_LINE_HEIGHT } from './Typography';

export const arrowIconURI = {
  normal:
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xNy43MDcxIDkuMjkyODlDMTguMDY3NiA5LjY1MzM4IDE4LjA5NTMgMTAuMjIwNiAxNy43OTAzIDEwLjYxMjlMMTcuNzA3MSAxMC43MDcxTDEyLjcwNzEgMTUuNzA3MUMxMi4zNDY2IDE2LjA2NzYgMTEuNzc5NCAxNi4wOTUzIDExLjM4NzEgMTUuNzkwM0wxMS4yOTI5IDE1LjcwNzFMNi4yOTI4OSAxMC43MDcxQzUuOTAyMzcgMTAuMzE2NiA1LjkwMjM3IDkuNjgzNDIgNi4yOTI4OSA5LjI5Mjg5QzYuNjUzMzggOC45MzI0MSA3LjIyMDYxIDguOTA0NjggNy42MTI5IDkuMjA5N0w3LjcwNzExIDkuMjkyODlMMTIgMTMuNTg1TDE2LjI5MjkgOS4yOTI4OUMxNi42NTM0IDguOTMyNDEgMTcuMjIwNiA4LjkwNDY4IDE3LjYxMjkgOS4yMDk3TDE3LjcwNzEgOS4yOTI4OVoiIGZpbGw9IiM3MTc2NzgiLz4KPC9zdmc+Cg==',
  hover:
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiPgogICAgPGRlZnM+CiAgICAgICAgPHBhdGggaWQ9ImEiIGQ9Ik0xLjA1MyAxOUwwIDE4LjExM2w4LjA3LTguNTI0TDAgMS4wNjUgMS4wNTMgMCAxMCA5LjU5eiIgdHJhbnNmb3JtPSJyb3RhdGUoOTApLCB0cmFuc2xhdGUoNCwgLTE0KSIgLz4KICAgIDwvZGVmcz4KICAgIDx1c2UgZmlsbD0iI0Y0NTMzNCIgZmlsbC1ydWxlPSJldmVub2RkIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxMSA3KSIgeGxpbms6aHJlZj0iI2EiLz4KPC9zdmc+Cg==',
};

interface BaseSelectProps extends HTMLProps<HTMLSelectElement> {
  title?: string;
  name?: string;
  value: string;
  onSelect: (event: ChangeEvent<HTMLSelectElement>) => void;
  onClick?: () => void;
  optionsV2?: boolean;
}

interface OutlineSelectProps extends Omit<BaseSelectProps, 'width'> {
  options: Array<string | number>;
  width?: ResponsiveValue<string | number>;
}

interface BorderlessSelectProps extends BaseSelectProps {
  options: Array<{ localized: string; option: string }>;
  className?: string;
  ariaLabel?: string;
}

export const BaseSelect = styled.select<any>`
  ${space}
  ${color}
  ${borderStyle}
  ${borderWidth}
  ${borderColor}
  ${border}
  ${fontSize}
  ${lineHeight}
  ${backgroundSize}
  ${backgroundPosition}

  margin: 0;
  appearance: none;
  background-color: ${themeGet('colors.sys.neutral.background.default')};
  background-image: url('${arrowIconURI.normal}');
  background-repeat: no-repeat, repeat;
  cursor: pointer;

  &:disabled {
    color: ${themeGet('colors.sys.neutral.border.default')};
    cursor: not-allowed;
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    border-color: ${themeGet('colors.sys.neutral.background.inverted')};
  }

  &:focus-visible {
    outline: 1px solid ${themeGet('colors.sys.secondary.border.focus')};
    border-color: ${themeGet('colors.sys.secondary.border.focus')};
  }

  &:hover {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.active')};
    border-color: ${themeGet('colors.sys.neutral.border.active')};
  }
`;

const StyledOutlineSelect = styled(BaseSelect)<any>`
  ${width}
  ${height}
  ${maxWidth}
  ${borderRadius}
`;

export const outlineSelectProps = {
  fontSize: TYPOGRAPHY_BR1_FONT_SIZE,
  lineHeight: TYPOGRAPHY_BR1_LINE_HEIGHT,
  height: ['38px', null, null, '40px'],
  pl: 'sp_8',
  color: 'sys.neutral.text.strong',
  borderRadius: 'small',
  backgroundSize: '24px auto, 100%',
  backgroundPosition: 'right 7px top 50%, 0 0',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.inactive',
};

export const OutlineSelect = ({
  title,
  name,
  value,
  onSelect,
  options = [],
  width: selectWidth = '82px',
  ...props
}: OutlineSelectProps) => (
  <StyledOutlineSelect
    data-testid="outline-select"
    title={title}
    name={name}
    value={value}
    onChange={onSelect}
    width={selectWidth}
    {...outlineSelectProps}
    {...props}
  >
    {options.map((val, index) => (
      <option value={val} key={index}>
        {val}
      </option>
    ))}
  </StyledOutlineSelect>
);

const StyledBorderlessSelectV2 = props =>
  props.optionsV2 &&
  css`
    font-size: inherit;
    background-color: inherit;
    padding-left: 96px;
    padding-right: 30px;
    height: 40px;

    ${breakpoint(2)(props)} {
      padding-left: 102px;
      height: 48px;
    }

    &:disabled {
      color: ${themeGet('colors.sys.neutral.border.default')(props)};
      cursor: not-allowed;
      background-color: ${themeGet('colors.sys.neutral.background.hover')(props)};
      border-color: ${themeGet('colors.sys.neutral.background.inverted')(props)};
    }
    &:focus-visible,
    &:hover,
    &:active {
      outline: none;
    }
  `;

const UnstyledStyledBorderlessSelect = styled(BaseSelect)<{ optionsV2: boolean }>`
  font-weight: bold;
  > option {
    font-weight: normal;
    color: ${themeGet('colors.sys.neutral.border.strong')};
  }
  ${StyledBorderlessSelectV2}
`;

const StyledBorderlessSelect = (props: ComponentProps<typeof UnstyledStyledBorderlessSelect>) => (
  <UnstyledStyledBorderlessSelect
    pr="sp_24"
    border="none"
    color="sys.neutral.border.strong"
    backgroundSize="24px auto, 100%"
    backgroundPosition="right 10px top 50%, 0 0"
    {...props}
  />
);

export const BorderlessSelect = ({
  title,
  name,
  value,
  options = [],
  onSelect,
  onClick,
  className,
  ariaLabel,
  optionsV2,
}: BorderlessSelectProps) => (
  <StyledBorderlessSelect
    data-testid="borderless-select"
    title={title}
    name={name}
    value={value}
    onChange={onSelect}
    className={className}
    aria-label={ariaLabel}
    optionsV2={optionsV2}
    {...(onClick ? { onClick } : {})}
  >
    {options.map(({ option, localized }, index) => (
      <option value={option} key={index}>
        {localized}
      </option>
    ))}
  </StyledBorderlessSelect>
);
